import { createContext, useContext, type ReactNode } from 'react';
import type { PortalBrandTheme } from '../../../types/portalReport';

// CLIENT PORTAL Phase 2 — the resolved brand, available to the whole portal.
//
// ⚠️ WHY A CONTEXT RATHER THAN A PROP. `PortalShell` is rendered from eight call
// sites — the home page's three states, the sign-in and finish pages, the
// report's loading/error/empty states, and the report layout — and threading the
// brand through every one of them would mean eight chances to forget it. A
// client whose report was branded and whose "something went wrong" page was not
// would notice, and the miss would be invisible in review.
//
// ⚠️ It carries what the server RESOLVED — never a value the browser chose. The
// brand comes from `/api/portal/me`, off the client's own scope.

export type PortalBrand = {
  theme: PortalBrandTheme | null;
  brandName: string | null;
};

const UNBRANDED: PortalBrand = { theme: null, brandName: null };

const Ctx = createContext<PortalBrand>(UNBRANDED);

export function PortalBrandProvider({
  brand,
  children,
}: {
  brand: PortalBrand;
  children: ReactNode;
}) {
  return <Ctx.Provider value={brand}>{children}</Ctx.Provider>;
}

/**
 * ⚠️ The DEFAULT is unbranded, so a component rendered outside the provider —
 * the sign-in page, which has no session and therefore no brand — gets kyck's
 * own look rather than throwing. "We don't know who you are yet" is a real
 * state, not an error.
 */
export function usePortalBrand(): PortalBrand {
  return useContext(Ctx);
}
